import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogContent,
  DialogDescription,
} from './dialog';
import {
  Drawer,
  DrawerTitle,
  DrawerClose,
  DrawerHeader,
  DrawerContent,
  DrawerDescription,
} from './drawer';
import React from 'react';
import { cn } from '@/lib/utils';
import { useDialog } from '@/context';
import { useMediaQuery } from '@/hooks';
import type { DialogComponentProps, DialogConfig } from '@gokrew/models/dialog';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants/translation-namespaces';

/**
 * A responsive dialog component that uses a dialog on large screens and a drawer on small screens.
 *
 * @returns The responsive dialog component
 */
export function ResponsiveDialog() {
  const matches = useMediaQuery('(min-width: 640px)');
  const { dialogs, closeDialog } = useDialog();
  const { t } = useTranslation(TranslationNS.LibsCommon);

  const dismissDialog = (dialog: DialogConfig) => {
    getContentOnClose(dialog.content)?.();
    closeDialog(dialog.id);
  };

  if (matches) {
    return (
      <React.Fragment>
        {dialogs.map((dialog) => (
          <Dialog
            open={true}
            key={dialog.id}
            onOpenChange={() => dismissDialog(dialog)}
          >
            <DialogContent
              showCloseButton={false}
              data-tour={dialog.dataTour}
              onInteractOutside={(e) => {
                if (!dialog.disableClose) return;
                const target = e.target as Element | null;
                if (target?.closest?.('[data-dialog-no-dismiss]')) return;
                e.preventDefault();
              }}
              onEscapeKeyDown={(e) =>
                dialog.disableClose && e.preventDefault()
              }
              onOpenAutoFocus={(e) => e.preventDefault()}
              className={cn(
                dialog.disableClose && '[&>button]:hidden',
                'w-[425px] !max-w-[95vw] max-h-[90vh] overflow-hidden',
                dialog.containerClass
              )}
            >
              <DialogHeader
                className={cn(
                  dialog.hideHeader && 'hidden',
                  'flex-row !mb-0 pb-4 sticky top-0 bg-background z-50'
                )}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <DialogTitle>{dialog.title || 'Attention'}</DialogTitle>
                  <DialogDescription
                    className={!dialog.description ? 'hidden' : ''}
                  >
                    {dialog.description}
                  </DialogDescription>
                </div>
                {attachDialogIdToContent(dialog.headerActionContent, dialog.id)}

                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  data-slot="dialog-close"
                  onClick={() => dismissDialog(dialog)}
                  className="flex items-center justify-center cursor-pointer text-gray-600"
                >
                  <XIcon size={20} />
                  <span className="sr-only">{t('dialog.close')}</span>
                </button>
              </DialogHeader>

              <div className="px-6 pb-6">
                {attachDialogIdToContent(dialog.content, dialog.id)}
              </div>
            </DialogContent>
          </Dialog>
        ))}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {dialogs.map((dialog) => (
        <Drawer
          open={true}
          key={dialog.id}
          onClose={() => dismissDialog(dialog)}
        >
          <DrawerClose></DrawerClose>
          <DrawerContent
            className={`sm:w-[425px] overflow-hidden ${dialog.containerClass}`}
          >
            <DrawerHeader className={cn(dialog.hideHeader && 'hidden')}>
              <div className="flex flex-row gap-16">
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <DrawerTitle>{dialog.title || 'Attention'}</DrawerTitle>
                  <DrawerDescription
                    className={!dialog.description ? 'hidden' : ''}
                  >
                    {dialog.description}
                  </DrawerDescription>
                </div>
                {attachDialogIdToContent(dialog.headerActionContent, dialog.id)}
              </div>
            </DrawerHeader>

            <div className="overflow-y-auto flex-1 p-4 xs:p-6 pt-1 xs:pt-2 pb-20">
              {attachDialogIdToContent(dialog.content, dialog.id)}
            </div>
          </DrawerContent>
        </Drawer>
      ))}
    </React.Fragment>
  );
}

/**
 * A function that maps over the given content and clones any elements that are
 * instances of `Pick` with the given `dialogId`. This is used to pass the `dialogId`
 * to the content of a dialog or drawer.
 *
 * `React.Fragment` children are unwrapped and recursed into, since
 * `React.cloneElement` on a fragment does not forward props to its children.
 *
 * @param {React.ReactNode} content - The content to map over
 * @param {string} dialogId - The id of the dialog to pass to the content
 * @returns {React.ReactNode} The cloned content with the dialogId passed
 */
const attachDialogIdToContent = (
  content: React.ReactNode,
  dialogId: string
): React.ReactNode =>
  React.Children.map(content, (child) => {
    if (!React.isValidElement<Pick<DialogComponentProps, 'dialogId'>>(child))
      return child;
    if (child.type === React.Fragment) {
      const fragmentProps = child.props as { children?: React.ReactNode };
      return attachDialogIdToContent(fragmentProps.children, dialogId);
    }
    return React.cloneElement(child, { dialogId });
  });

/**
 * Finds the `onClose` handler on a dialog's content, so it can be invoked
 * whenever the dialog is dismissed by a means other than the content's own
 * buttons — the header X, Escape, an outside click, or the drawer's close.
 * Without this, only a content-rendered "Cancel" button ever runs cleanup.
 *
 * Multiple children and `React.Fragment` children are both handled, matching
 * `attachDialogIdToContent`; the first `onClose` found wins.
 */
const getContentOnClose = (
  content: React.ReactNode
): (() => void) | undefined => {
  for (const child of React.Children.toArray(content)) {
    if (!React.isValidElement<Pick<DialogComponentProps, 'onClose'>>(child))
      continue;
    if (child.type === React.Fragment) {
      const fragmentProps = child.props as { children?: React.ReactNode };
      const nested = getContentOnClose(fragmentProps.children);
      if (nested) return nested;
      continue;
    }
    if (child.props.onClose) return child.props.onClose;
  }
  return undefined;
};
